<script setup lang="ts">
/**
 * A ribbon „big button“ split in two: the face fires the main action
 * (`main`), the caret strip opens a menu (default slot) — „Öffnen“ opens the
 * file chooser, its caret lists recent documents. The menu is the same kit
 * menu panel as NxRibbonMenuButton's; `open-menu` fires on every opening so
 * the host can load its entries lazily.
 */
import { ref, watch } from 'vue';
import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js';
import NxMenuPanel from '../ContextMenu/NxMenuPanel.vue';

const props = withDefaults(
  defineProps<{
    label: string;
    title?: string;
    /** Title and accessible name of the caret. */
    menuTitle?: string;
    disabled?: boolean;
    menuWidth?: 'sm' | 'md' | 'lg' | 'auto';
  }>(),
  { title: undefined, menuTitle: undefined, disabled: false, menuWidth: 'md' }
);

const emit = defineEmits<{ main: []; 'open-menu': [] }>();

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const caret = ref<HTMLButtonElement | null>(null);

function toggle() {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) emit('open-menu');
}

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) open.value = false;
  }
);
</script>

<template>
  <span
    ref="root"
    class="nx-ribbon-split"
    :class="{ 'is-on': open, 'is-disabled': props.disabled }"
  >
    <!-- mousedown.prevent: the command keeps the document's focus (a grid's
         active cell), like the caret and every other ribbon command. -->
    <button
      type="button"
      class="nx-ribbon-split__main"
      :title="props.title"
      :disabled="props.disabled"
      data-testid="nx-ribbon-split-main"
      @mousedown.prevent
      @click.prevent="emit('main')"
    >
      <span class="nx-ribbon-split__icon"><slot name="icon" /></span>
      <span class="big-label nx-ribbon-split__label">{{ props.label }}</span>
    </button>
    <button
      ref="caret"
      type="button"
      class="nx-ribbon-split__caret"
      :title="props.menuTitle ?? props.title"
      :aria-label="props.menuTitle ?? props.title ?? props.label"
      :aria-expanded="open"
      :disabled="props.disabled"
      aria-haspopup="menu"
      data-testid="nx-ribbon-split-caret"
      @mousedown.prevent="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      @keydown.down.prevent="!open && toggle()"
    >
      <ChevronDown class="nxt-icon-2xs" aria-hidden="true" />
    </button>
  </span>
  <NxMenuPanel
    :open="open"
    :reference="root"
    placement="bottom-start"
    :offset="2"
    :width="props.menuWidth"
    :aria-label="props.menuTitle ?? props.title ?? props.label"
    :ignore="[root]"
    :return-focus-to="caret"
    @close="open = false"
  >
    <slot />
  </NxMenuPanel>
</template>

<style scoped>
.nx-ribbon-split {
  display: inline-flex;
  align-items: stretch;
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-md);
  transition: border-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-ribbon-split:hover:not(.is-disabled),
.nx-ribbon-split.is-on {
  border-color: var(--office-ribbon-border, var(--nxt-border));
}
.nx-ribbon-split__main {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: calc(var(--nxt-size-unit) * 0.1875);
  min-width: calc(var(--nxt-size-unit) * 3.25);
  height: var(--office-ribbon-big-action-height, var(--nxt-tile-size-xl));
  padding: calc(var(--nxt-size-unit) * 0.1875) calc(var(--nxt-size-unit) * 0.375);
  border: none;
  border-radius: var(--nxt-radius-md) 0 0 var(--nxt-radius-md);
  background: transparent;
  color: var(--office-ribbon-fg, var(--nxt-fg));
  cursor: pointer;
}
.nx-ribbon-split__icon {
  display: inline-flex;
}
.nx-ribbon-split__label {
  /* The big-command label: small type under the icon, wrapped at the ribbon's
     label width rather than widening the button. */
  max-width: var(--office-ribbon-big-label-max, calc(var(--nxt-size-unit) * 4.25));
  /* Two lines' room like NxRibbonButton's large label, a one-line label
     centred in it, so this icon sits at the height of its neighbours' (I-3178). */
  display: grid;
  align-content: center;
  min-block-size: 2lh;
  color: inherit;
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  line-height: var(--nxt-line-height-tight);
  text-align: center;
  white-space: normal;
  hyphens: auto;
  hyphenate-limit-chars: 15 6 6;
}
.nx-ribbon-split__caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--nxt-size-unit) * 1.125);
  padding: 0;
  border: none;
  border-inline-start: var(--nxt-hairline) solid transparent;
  border-radius: 0 var(--nxt-radius-md) var(--nxt-radius-md) 0;
  background: transparent;
  color: var(--nxt-fg-muted);
  cursor: pointer;
}
.nx-ribbon-split:hover:not(.is-disabled) .nx-ribbon-split__caret {
  border-inline-start-color: var(--office-ribbon-border, var(--nxt-border));
}
.nx-ribbon-split__main:hover:not(:disabled),
.nx-ribbon-split__caret:hover:not(:disabled),
.nx-ribbon-split.is-on .nx-ribbon-split__caret {
  background: var(--office-ribbon-hover-bg, var(--nxt-brand-subtle-bg));
  color: var(--office-ribbon-hover-fg, var(--nxt-brand-subtle-fg));
}
.nx-ribbon-split__main:focus-visible,
.nx-ribbon-split__caret:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}
.nx-ribbon-split.is-disabled {
  opacity: 0.4;
}
.nx-ribbon-split__main:disabled,
.nx-ribbon-split__caret:disabled {
  cursor: not-allowed;
}
@media (prefers-reduced-motion: reduce) {
  .nx-ribbon-split {
    transition: none;
  }
}
</style>
